// src/components/RegisterModal.js
import React, { useState, useEffect, useRef } from 'react';
import { createUserWithEmailAndPassword, sendEmailVerification } from 'firebase/auth';
import { doc, setDoc } from 'firebase/firestore';
import { auth, db } from '../firebase';
import './RegisterModal.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUserClock } from '@fortawesome/free-solid-svg-icons'; // بس دي اللي محتاجينها

const RegisterModal = ({ currentLanguage, getLocalizedText, onClose }) => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isVerifiedSent, setIsVerifiedSent] = useState(false);
  const modalRef = useRef(null);
  const [showPassword, setShowPassword] = useState(false);

  const t = (ar, en) => getLocalizedText(ar, en);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (modalRef.current && !modalRef.current.contains(e.target)) onClose();
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  useEffect(() => {
    const handleEsc = (e) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', handleEsc);
    return () => document.removeEventListener('keydown', handleEsc);
  }, [onClose]);

  const isValid =
    firstName.trim() &&
    lastName.trim() &&
    email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/) &&
    password.length >= 8 &&
    password === confirmPassword;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      const fullName = `${firstName} ${lastName}`;
      await setDoc(doc(db, 'unverified_users', user.uid), {
        firstName,
        lastName,
        fullName,
        email,
        createdAt: new Date().toISOString(),
      });

const getVerificationRedirectUrl = () => {
  const origin = window.location.origin;
  
  // في الديفيلوپمنت المحلي: نرجّع localhost عشان Firebase يقبل
  if (origin.includes('localhost') || origin.includes('127.0.0.1') || origin.includes('192.168.')) {
    return 'http://localhost:3000';
  }
  
  // في البرودكشن: نرجّع الدومين الحقيقي
  return origin;
};

await sendEmailVerification(user, {
  url: getVerificationRedirectUrl(),
});

      setIsVerifiedSent(true);
    } catch (err) {
      let errorMsg;
      if (currentLanguage === 'ar') {
        switch (err.code) {
          case 'auth/email-already-in-use':
            errorMsg = 'هذا البريد الإلكتروني مستخدم بالفعل.';
            break;
          case 'auth/invalid-email':
            errorMsg = 'البريد الإلكتروني غير صالح.';
            break;
          case 'auth/weak-password':
            errorMsg = 'كلمة المرور يجب أن تكون 8 أحرف على الأقل.';
            break;
          default:
            errorMsg = 'حدث خطأ أثناء إنشاء الحساب. حاول مرة أخرى.';
        }
      } else {
        switch (err.code) {
          case 'auth/email-already-in-use':
            errorMsg = 'This email is already existing.';
            break;
          case 'auth/invalid-email':
            errorMsg = 'Invalid email address.';
            break;
          case 'auth/weak-password':
            errorMsg = 'Password should be at least 8 characters.';
            break;
          default:
            errorMsg = 'An error occurred. Please try again.';
        }
      }
      setError(errorMsg);
    } finally {
      setIsLoading(false);
    }
  };

  // أهم حاجة: شاشة النجاح تيجي الأول
  if (isVerifiedSent) {
  return (
    <div className="register-modal-overlay">
      <div className={`register-modal success-mode ${currentLanguage === 'ar' ? 'ar' : 'en'}`} ref={modalRef}>
        <button className="register-modal-close" onClick={onClose}>×</button>

        <FontAwesomeIcon 
          icon={faUserClock} 
          className="register-success-icon" 
        />

        <p className="register-success-message">
          {t(
            'تم إرسال رابط تفعيل الحساب إلى بريدك الإلكتروني. يرجى التحقق من صندوق الوارد ومجلد السبام لتفعيل حسابك.',
            'A verification link has been sent to your email. Please check your inbox and spam folder to verify your account.'
          )}
        </p>
        </div>
      </div>
    );
  }

  // نموذج التسجيل العادي (لو لسه ما اتعملش التفعيل)
  return (
    <div className="register-modal-overlay">
      <div className={`register-modal ${currentLanguage === 'ar' ? 'ar' : 'en'}`} ref={modalRef}>
        <button className="register-modal-close" onClick={onClose}>×</button>

        <div className="register-modal-logo">
          <img src="/assets/logo.png" alt="ROOMATE" />
        </div>

        <h2 className="register-modal-title">
          {t('إنشاء حساب جديد', 'Create New Account')}
        </h2>

        <form className="register-modal-form" onSubmit={handleSubmit}>
          <div className="register-modal-field">
            <label>{t('الاسم الأول', 'First Name')}</label>
            <input
              type="text"
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder={t('أدخل اسمك الأول', 'Enter your first name')}
              dir={currentLanguage === 'ar' ? 'rtl' : 'ltr'}
              required
            />
          </div>

          <div className="register-modal-field">
            <label>{t('الاسم الأخير', 'Last Name')}</label>
            <input
              type="text"
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder={t('أدخل اسمك الأخير', 'Enter your last name')}
              dir={currentLanguage === 'ar' ? 'rtl' : 'ltr'}
              required
            />
          </div>

          <div className="register-modal-field">
            <label>{t('البريد الإلكتروني', 'Email')}</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t('أدخل بريدك الإلكتروني', 'Enter your email')}
              dir={currentLanguage === 'ar' ? 'rtl' : 'ltr'}
              required
            />
          </div>

          <div className="register-modal-field">
            <label>{t('كلمة المرور', 'Password')}</label>
            <input
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t('8 أحرف على الأقل', 'At least 8 characters')}
              dir={currentLanguage === 'ar' ? 'rtl' : 'ltr'}
              required
            />
          </div>

          <div className="register-modal-field">
            <label>{t('تأكيد كلمة المرور', 'Confirm Password')}</label>
            <input
              type={showPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              placeholder={t('تأكيد كلمة المرور', 'Confirm password')}
              dir={currentLanguage === 'ar' ? 'rtl' : 'ltr'}
              required
            />
          </div>

          <div className="password-toggle-container">
            <label className="password-toggle-label">
              <input
                type="checkbox"
                checked={showPassword}
                onChange={(e) => setShowPassword(e.target.checked)}
              />
             <span className="password-toggle-text">
               {t('إظهار كلمة المرور', 'Show password')}
             </span>
           </label>
          </div>

          {error && <div className="register-modal-error">{error}</div>}

          <button
            type="submit"
            className={`register-modal-submit ${isValid && !isLoading ? 'active' : ''}`}
            disabled={!isValid || isLoading}
          >
            {isLoading ? t('جاري الإنشاء...', 'Creating...') : t('إنشاء الحساب', 'Create Account')}
          </button>
        </form>

        <div className="register-modal-links">
          <button 
            type="button" 
            onClick={() => onClose('login')}  // ده السحر
            className="register-modal-link"
          >
            {t('لديك حساب بالفعل؟ تسجيل الدخول', 'Already have an account? Login')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RegisterModal;